import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Avatar,
  ListItemAvatar,
  IconButton,
  Snackbar,
  Alert,
  Collapse,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "axios";
import { baseUrl } from "../../constants";
import { handleDateWithTime } from "../../utils/date-handlers";

export interface Comment {
  id: number;
  user: {
    username: string;
    imageUrl?: string;
  };
  text: string;
  date: string;
}

interface CommentDialogProps {
  open: boolean;
  onClose: () => void;
  taskId: number;
}

const CommentDialog: React.FC<CommentDialogProps> = ({
  open,
  onClose,
  taskId,
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newCommentText, setNewCommentText] = useState("");
  const [newCommentImage, setNewCommentImage] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const theme = useTheme(); // ⬅️ Add this line

  const fetchComments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("accessToken");
      const res = await axios.get<Comment[]>(
        `${baseUrl}/task-comment/task/${taskId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setComments(res.data);
    } catch (err) {
      console.error("Failed to fetch comments", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCommentSubmit = async () => {
    if (!newCommentText.trim()) return;

    const formData = new FormData();
    formData.append("text", newCommentText);
    formData.append("taskId", taskId.toString());
    if (newCommentImage) {
      formData.append("image", newCommentImage);
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem("accessToken");
      await axios.post(`${baseUrl}/task-comment`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      setNewCommentText("");
      setNewCommentImage(null);
      fetchComments();
    } catch (error) {
      console.error("Error submitting comment:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      const token = localStorage.getItem("accessToken");
      await axios.delete(`${baseUrl}/task-comment/${commentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSnackbar({
        open: true,
        message: "Comment Deleted successfully!",
        severity: "success",
      });
      fetchComments();
    } catch (error: any) {
      console.error("Error Deleting comment:", error);
      setSnackbar({
        open: true,
        message: `Failed To Delete Comment ${
          error.response?.data?.message || ""
        }`,
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (open) fetchComments();
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { bgcolor: theme.palette.background.default },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Task Comments</DialogTitle>
      <DialogContent
        dividers
       sx={{
    bgcolor: theme.palette.background.paper,
    p: 2,
    maxHeight: "60vh", // adjust based on your app layout
    overflowY: "auto",
  }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Comments
          </Typography>
          <Button
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            size="small"
          >
            {expanded ? "Hide" : "Show"} Comments
          </Button>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {loading ? (
            <Box display="flex" justifyContent="center" my={2}>
              <CircularProgress size={20} />
            </Box>
          ) : comments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No comments yet.
            </Typography>
          ) : (
            <List>
              {comments.map((comment) => (
                <Paper
                  key={comment.id}
                  elevation={1}
                  sx={{
                    mb: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: theme.palette.background.paper,
                  }}
                >
                  <ListItem
                    alignItems="flex-start"
                    disableGutters
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteComment(comment.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={comment.user.imageUrl || undefined}
                        sx={{
                          width: 30,
                          height: 30,
                          bgcolor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                        }}
                      >
                        {comment.user.username?.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          fontWeight={600}
                          fontSize={13}
                          color="text.primary"
                        >
                          {comment.user.username}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography
                            variant="body2"
                            color="text.primary"
                            fontSize={13}
                          >
                            {comment.text}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {handleDateWithTime(comment.date)}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                </Paper>
              ))}
            </List>
          )}
        </Collapse>

       <Box mt={2} mb={1}>
          <TextField
            label="Add a comment..."
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            multiline
            fullWidth
            rows={2}
            variant="outlined"
            sx={{
              borderRadius: 2,
              bgcolor: theme.palette.background.paper,
              "& .MuiInputBase-root": {
                bgcolor: theme.palette.background.paper,
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} color="inherit" size="small">
          Cancel
        </Button>
        <Button
          onClick={handleCommentSubmit}
          color="primary"
          variant="contained"
          endIcon={<SendIcon />}
          disabled={submitting}
          size="small"
        >
          {submitting ? <CircularProgress size={18} /> : "Post"}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default CommentDialog;
